import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as FileIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import {
  getValidationConfig,
  validateUploadRequest,
  uploadDataset,
  getUserStorageUsage,
  ValidationConfig,
  FileUploadInfo,
} from '../../utils/dataApi';

interface CreateDatasetModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onDatasetCreated: () => void;
}

interface StorageInfo {
  total_size_gb: number;
  subscription_limit_gb: number;
  subscription_tier: string;
  usage_percentage: number;
}

export const CreateDatasetModal: React.FC<CreateDatasetModalProps> = ({
  open,
  onClose,
  projectId,
  onDatasetCreated,
}) => {
  const [datasetName, setDatasetName] = useState('');
  const [classificationMethod, setClassificationMethod] = useState<'by_device' | 'by_type'>('by_device');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [validationConfig, setValidationConfig] = useState<ValidationConfig | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  // Load validation config and storage info when modal opens
  useEffect(() => {
    if (open) {
      loadValidationConfig();
      loadStorageInfo();
      resetForm();
    }
  }, [open, classificationMethod]);

  const resetForm = () => {
    setDatasetName('');
    setSelectedDevice('');
    setFiles([]);
    setValidationResult(null);
    setError(null);
    setUploading(false);
    setUploadProgress({ completed: 0, total: 0 });
  };

  const loadValidationConfig = async () => {
    try {
      const config = await getValidationConfig(classificationMethod);
      setValidationConfig(config);
      setSelectedDevice('');
    } catch (err) {
      console.error('Error loading validation config:', err);
      setError('Failed to load validation configuration');
    }
  };

  const loadStorageInfo = async () => {
    try {
      const usage = await getUserStorageUsage();
      setStorageInfo({
        total_size_gb: usage.total_size_gb,
        subscription_limit_gb: usage.subscription_limit_gb,
        subscription_tier: usage.subscription_tier,
        usage_percentage: (usage.total_size_gb / usage.subscription_limit_gb) * 100
      });
    } catch (err) {
      console.error('Error loading storage info:', err);
    }
  };

  const validateFiles = async () => {
    if (!selectedDevice || files.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const fileInfos: FileUploadInfo[] = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
      }));

      const result = await validateUploadRequest({
        files: fileInfos,
        classification_method: classificationMethod,
        data_type_or_device: selectedDevice,
      });

      setValidationResult(result);
    } catch (err) {
      console.error('Error validating files:', err);
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!datasetName || !selectedDevice || files.length === 0 || !validationResult?.valid) return;

    try {
      setUploading(true);
      setError(null);
      setUploadProgress({ completed: 0, total: files.length });

      const completedDataset = await uploadDataset(
        projectId,
        {
          dataset_name: datasetName,
          classification_method: classificationMethod,
          data_type_or_device: selectedDevice,
          files: files,
        },
        (completed, total) => {
          setUploadProgress({ completed, total });
        }
      );

      // Success - dataset is now fully populated with file metadata
      console.log('Upload completed successfully:', {
        datasetId: completedDataset.data_id,
        datasetName: completedDataset.dataset_name,
        fileCount: completedDataset.file_count,
        totalSize: completedDataset.total_size_bytes
      });
      
      onDatasetCreated();
      onClose();
    } catch (err) {
      console.error('Error uploading dataset:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      console.log('Files dropped/selected:', acceptedFiles); // Add this
      setFiles(prev => [...prev, ...acceptedFiles]);
      setValidationResult(null);
    },
    disabled: uploading,
    // Add these debug props:
    onDropAccepted: (files) => console.log('Files accepted:', files),
    onDropRejected: (rejections) => console.log('Files rejected:', rejections),
    onFileDialogCancel: () => console.log('File dialog cancelled'),
    onFileDialogOpen: () => console.log('File dialog opened'),
  });

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setValidationResult(null);
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getUploadSizeGB = () => {
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    return totalBytes / (1024 * 1024 * 1024);
  };

  const wouldExceedLimit = () => {
    if (!storageInfo) return false;
    const uploadSizeGB = getUploadSizeGB();
    return (storageInfo.total_size_gb + uploadSizeGB) > storageInfo.subscription_limit_gb;
  };

  const canUpload = () => {
    return datasetName.trim().length > 0 && 
           selectedDevice.length > 0 && 
           files.length > 0 && 
           validationResult?.valid && 
           !wouldExceedLimit() &&
           !uploading;
  };

  const isOptionEnabled = (option: any): boolean => {
    return option.enabled !== false;
  };

return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 600 } }}
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>
        <Typography sx={{ fontWeight: 600 }}>
          Upload Dataset
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload and validate your ecological data files
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Storage Info */}
        {storageInfo && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: wouldExceedLimit() ? 'error.50' : 'grey.50' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <StorageIcon color={wouldExceedLimit() ? 'error' : 'primary'} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Storage Usage ({storageInfo.subscription_tier.toUpperCase()} Plan)
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(storageInfo.usage_percentage, 100)}
              color={storageInfo.usage_percentage > 80 ? 'warning' : 'primary'}
              sx={{ height: 6, borderRadius: 1, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {storageInfo.total_size_gb.toFixed(2)} GB / {storageInfo.subscription_limit_gb} GB used
            </Typography>
            {files.length > 0 && (
              <Typography variant="body2" color={wouldExceedLimit() ? 'error.main' : 'text.secondary'}>
                Upload will add: {getUploadSizeGB().toFixed(2)} GB
              </Typography>
            )}
            {wouldExceedLimit() && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                Upgrade your plan in Settings to upload larger datasets
              </Typography>
            )}
          </Paper>
        )}

        {/* Dataset Configuration */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Dataset Name"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            placeholder="e.g., Bird Songs - Summer 2024"
            sx={{ mb: 2 }}
            disabled={uploading}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Classification Method</InputLabel>
            <Select
              value={classificationMethod}
              onChange={(e) => setClassificationMethod(e.target.value as 'by_device' | 'by_type')}
              label="Classification Method"
              disabled={uploading}
            >
              <MenuItem value="by_device">By Device</MenuItem>
              <MenuItem value="by_type" disabled>By Data Type (Coming Soon!)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Device</InputLabel>
            <Select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              label="Device"
              disabled={!validationConfig || uploading}
            >
              {validationConfig?.options.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  disabled={!isOptionEnabled(option)}
                >
                  <Box>
                    <Typography>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isOptionEnabled(option) ? `Accepts: ${option.extensions.join(', ')}` : 'Coming soon'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* File Upload */}
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            bgcolor: isDragActive ? 'primary.50' : 'grey.50',
            cursor: uploading ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            opacity: uploading ? 0.6 : 1,
          }}
        >
          <input {...getInputProps()} />
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {uploading ? 'Uploading...' : isDragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
          </Typography>
          {selectedDevice && validationConfig && (
            <Typography variant="body2" color="text.secondary">
              Accepted types: {validationConfig.options.find(opt => opt.value === selectedDevice)?.extensions.join(', ')}
            </Typography>
          )}
        </Paper>

        {/* Files List */}
        {files.length > 0 && (
          <Paper sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>
              Selected Files ({files.length}) - {formatFileSize(files.reduce((sum, file) => sum + file.size, 0))}
            </Typography>
            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
              {files.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  secondaryAction={
                    !uploading && (
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon>
                    <FileIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Validation Results */}
        {validationResult && (
          <Alert
            severity={validationResult.valid ? 'success' : 'error'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
            </Typography>
            {validationResult.issues.length > 0 && (
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationResult.issues.map((issue: string, index: number) => (
                  <li key={index}>
                    <Typography variant="body2">{issue}</Typography>
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Uploading Files: {uploadProgress.completed} of {uploadProgress.total}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(uploadProgress.completed / uploadProgress.total) * 100}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Paper>
        )}

        {/* Upload Disclaimer - shown during upload */}
        {uploading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Upload in Progress
            </Typography>
            <Typography variant="body2">
              Please keep this window open until upload completes. Closing the browser or navigating away will cancel the upload and no data will be saved.
            </Typography>
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Storage Limit Warning */}
        {wouldExceedLimit() && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This upload would exceed your storage limit. Please remove some files or upgrade your plan.
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={uploading}
          sx={{ 
            opacity: uploading ? 0.5 : 1,
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? 'Upload in Progress...' : 'Cancel'}
        </Button>
        <Button
          onClick={validateFiles}
          disabled={files.length === 0 || !selectedDevice || loading || uploading}
          variant="outlined"
        >
          {loading ? 'Validating...' : 'Validate Files'}
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!canUpload()}
          variant="contained"
        >
          {uploading ? 'Uploading...' : 'Upload Dataset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};